import * as React from "react";

import { Clipboard, ImageIcon, Upload, X } from "lucide-react";

import { readDroppedImageUrl } from "@/components/fields/image-drag";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PhotoUploadProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  className?: string;
  accept?: string;
  maxSize?: number; // in MB
  transformToWebP?: boolean;
  /**
   * Called when the drop carries a dragged image's URL instead of a file.
   *
   * A browser hands a drop the bytes only for some images: a picture dragged
   * from elsewhere in the page usually arrives as a URL in the drag payload
   * and an EMPTY file list, so a file-only drop handler ignores it silently.
   * Given this prop, the field reports the URL to the caller instead of
   * dropping it — the caller knows whether that URL is meaningful to it (it
   * must be reachable by whatever fetches it later, which is why the field
   * does not guess). Left unset, the field keeps its file-only behaviour.
   */
  onDropUrl?: (url: string) => void;
}

export function PhotoUpload({
  value,
  onChange,
  className,
  accept = "image/*",
  maxSize = 5,
  transformToWebP = false,
  onDropUrl,
}: PhotoUploadProps) {
  const [preview, setPreview] = React.useState<string | null>(value || null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropzoneRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setPreview(value || null);
  }, [value]);

  const processFile = React.useCallback(
    (file: File) => {
      setError(null);

      if (!file.type.startsWith("image/")) {
        setError("Please upload an image file");
        return;
      }

      if (file.size > maxSize * 1024 * 1024) {
        setError(`File size must be less than ${maxSize}MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;

        if (transformToWebP) {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(img, 0, 0);
            const webpDataUrl = canvas.toDataURL("image/webp");
            setPreview(webpDataUrl);
            onChange?.(webpDataUrl);
          };
          img.src = result;
        } else {
          setPreview(result);
          onChange?.(result);
        }
      };
      reader.readAsDataURL(file);
    },
    [maxSize, onChange, transformToWebP],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
      return;
    }

    const url = readDroppedImageUrl(e.dataTransfer);
    if (url) {
      setError(null);
      onDropUrl?.(url);
    }
  };

  const handlePaste = React.useCallback(
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            processFile(file);
            break;
          }
        }
      }
    },
    [processFile],
  );

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((type) => type.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], "pasted-image.png", {
            type: imageType,
          });
          processFile(file);
          break;
        }
      }
    } catch {
      setError("Unable to read from clipboard. Try Ctrl+V instead.");
    }
  };

  React.useEffect(() => {
    const element = dropzoneRef.current;
    if (element) {
      document.addEventListener("paste", handlePaste);
      return () => document.removeEventListener("paste", handlePaste);
    }
  }, [handlePaste]);

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    onChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className={cn("w-full", className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="sr-only"
        aria-label="Upload photo"
      />

      {preview ? (
        // A drop target in its own right: once a preview exists the dashed box
        // is gone, and without this a second drop — the curator swapping the
        // image they just picked — would land on nothing.
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "relative group rounded-lg overflow-hidden border bg-muted",
            isDragOver ? "border-primary ring-2 ring-primary" : "border-border",
          )}
        >
          <img
            src={preview || "/placeholder.svg"}
            alt="Uploaded preview"
            className="w-full h-64 object-contain bg-muted"
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleRemove}
            aria-label="Remove photo"
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <div
          ref={dropzoneRef}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Image drop zone"
          className={cn(
            "relative flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer",
            isDragOver
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50",
          )}
        >
          <div className="flex items-center justify-center size-16 rounded-full bg-muted">
            <ImageIcon className="size-8 text-muted-foreground" />
          </div>

          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-foreground">
              Drop an image here, or click to upload
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, GIF up to {maxSize}MB
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Upload className="size-4" />
              Browse Files
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handlePasteFromClipboard();
              }}
            >
              <Clipboard className="size-4" />
              Paste from Clipboard
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            or press{" "}
            <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border rounded">
              Ctrl
            </kbd>{" "}
            +{" "}
            <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border rounded">
              V
            </kbd>{" "}
            to paste
          </p>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
}
